'use client'

import React from 'react'
import styles from './styles.module.css'
import ProgressiveImage from '../../ProgressiveImage'
import SmartLink from '../../../SmartLink'
import useSaleClock from '../../../SaleCampaign/useSaleClock'
import { useRegionOffers } from '../../../RegionOffers'
import { useUrlRegion } from '../../../../utils/useCurrentRegion'
import { buildCheckoutPageUrlFromProductSetId } from '../../../../utils/shopItems'
import {
  getCampaignPhase, getLimitedDeals, getLimitedDealState, dealPriceOf, dealStartMs, getDealGroups,
  PHASE_LIVE, PHASE_TEASER, DEAL_UPCOMING, DEAL_LIVE, DEAL_PENDING, DEAL_SOLD_OUT,
} from '../../../../lib/saleCampaign'
import {
  formatSaleDay, formatSaleMoment, itemTypeOf, mainImageOf, regularPriceOf, salePriceOf, savingOf,
} from '../saleFormat'
import SaleBonusPill from '../../../SaleCampaign/SaleBonusPill'
import DealPrice from '../../../SaleCampaign/DealPrice'
import DealCountdown from '../../../SaleCampaign/DealCountdown'

// The limited-stock pedal deals on /<region>/<campaign.route> (anchor #limited-deals).
//
// Every deal is on the page for the whole sale, with its price and its number
// of units, from the teaser on. No timers: a deal ends only when its units are
// gone (getLimitedDealState), and then the card says so and shows the Black
// Friday price the item is back at.
//
// Price rules, so the page never shows a number the checkout will not charge:
//  - live / sold out: what the backend offer charges right now
//  - teaser: the PUBLISHED deal price from the campaign doc, labelled with the
//    moment it goes live
//  - pending (sale live, backend price not loaded yet): no price at all
function SaleLimitedDeals({ saleCampaign: campaign, saleNowMs, saleClockOffsetMs, titleOverride }) {
  const { nowMs, mounted } = useSaleClock(saleNowMs, saleClockOffsetMs, 60000)
  const region = useUrlRegion()
  const { offers } = useRegionOffers()
  const phase = getCampaignPhase(campaign, nowMs)
  if (phase !== PHASE_LIVE && phase !== PHASE_TEASER) return null

  const deals = getLimitedDeals(campaign, region, nowMs)
  if (!deals.length) return null
  // Deal sets (pedals, wheels) each get a heading and their own timer; the
  // timers tick by themselves (DealCountdown live), the page clock is coarse.
  const groups = getDealGroups(campaign, nowMs, region)
  const sets = groups.length ? groups : [{ key: 'all', deals }]
  const byType = new Map((offers || []).map((o) => [itemTypeOf(o), o]))
  // Sets of one deal (the DFP15, the Lite Formula wheel) pair up side by side,
  // each still its own set with its own heading and timer; an odd one out takes
  // the full row.
  const shown = (set) => set.deals.filter((d) => { const o = byType.get(d.itemType); return o && o.price }).length
  const singles = sets.filter((set) => shown(set) === 1)
  const setClass = (set) => {
    if (shown(set) !== 1) return styles.set
    const pairs = singles.length - (singles.length % 2)
    return [styles.set, singles.indexOf(set) < pairs ? styles.setHalf : styles.setSingleWide].join(' ')
  }
  const copy = campaign.limited || {}
  // The free extra is shown from the teaser on: it is part of the published offer.
  const bonus = campaign.bonus && campaign.bonus.label
    ? { label: campaign.bonus.label, short: campaign.bonus.pillLabel || campaign.bonus.label, worth: (campaign.bonus.worth && (campaign.bonus.worth[region] || campaign.bonus.worth.world)) || null }
    : null

  return (
    <section id="limited-deals" className={styles.wrap} aria-labelledby="limited-deals-title">
      <div className={styles.inner}>
        <header className={styles.header}>
          <span className={styles.kicker}>{copy.kicker || 'Limited stock'}</span>
          <h2 id="limited-deals-title" className={styles.title}>
            {titleOverride || copy.title || 'Pedal deals, while units last'}
          </h2>
          <p className={styles.lead}>
            {phase === PHASE_TEASER
              ? (copy.teaserLead || `These prices go live ${formatSaleMoment(campaign.start)}, for a fixed number of units each.`)
              : (copy.lead || 'A fixed number of units at this price. When they are gone, the deal ends.')}
          </p>
        </header>

        <div className={styles.sets}>
        {sets.map((set) => (
        <div key={set.key} className={setClass(set)}>
          {set.title && (
            <div className={styles.setHead}>
              <h3 className={styles.setTitle}>{set.title}</h3>
              <DealCountdown
                label={(set.live ? set.countdownLive : set.countdownTeaser) || (set.live ? 'Deals end in' : 'Deals start in')}
                targetMs={set.targetMs}
                nowMs={nowMs}
                mounted={mounted}
                live
              />
            </div>
          )}
        <ol className={styles.cards}>
          {set.deals.map((deal) => {
            const item = byType.get(deal.itemType)
            if (!item || !item.price) return null
            const state = getLimitedDealState(campaign, nowMs, region, deal, item)
            const href = buildCheckoutPageUrlFromProductSetId(item.productSetId, region)
            const regular = regularPriceOf(item)
            const published = dealPriceOf(deal, region, regular && regular.currency)
            const unavailable = item.available === false

            let price = null
            let was = null
            if (state === DEAL_LIVE || state === DEAL_SOLD_OUT) {
              price = salePriceOf(item)
              was = savingOf(item) ? regular : null
            } else if (state === DEAL_UPCOMING && published && regular && published.priceCents < regular.priceCents) {
              price = published
              was = regular
            }

            const badge = {
              [DEAL_LIVE]: copy.badgeLive || 'Limited stock',
              [DEAL_UPCOMING]: copy.badgeUpcoming || `From ${formatSaleDay(dealStartMs(campaign, deal))}`,
              [DEAL_PENDING]: copy.badgeLive || 'Limited stock',
              [DEAL_SOLD_OUT]: copy.badgeSoldOut || 'Deal sold out',
            }[state]

            return (
              <li key={deal.itemType} className={[styles.card, state === DEAL_SOLD_OUT ? styles.cardSoldOut : ''].join(' ')}>
                <div className={styles.media}>
                  <div className={styles.productArt}>
                    <ProgressiveImage {...mainImageOf(item)} alt={item.name} sizes="(min-width: 1024px) 33vw, 100vw"/>
                  </div>
                  {badge && <span className={[styles.badge, state === DEAL_SOLD_OUT ? styles.badgeMuted : ''].join(' ')}>{badge}</span>}
                </div>
                <div className={styles.body}>
                  <h3 className={styles.itemName}>{deal.title || item.namePublicShop || item.name}</h3>
                  {deal.pitch && <p className={styles.pitch}>{deal.pitch}</p>}

                  {price && <DealPrice price={price} regular={was} size="lg" className={styles.priceRow}/>}

                  {state === DEAL_UPCOMING && (
                    <p className={styles.note}>Goes live {formatSaleMoment(dealStartMs(campaign, deal))}</p>
                  )}
                  {state === DEAL_PENDING && (
                    <p className={styles.note}>The deal price is being loaded. Check back in a few minutes.</p>
                  )}
                  {state === DEAL_SOLD_OUT ? (
                    <p className={styles.units}>
                      {copy.soldOutLine || 'All deal units are sold.'}
                    </p>
                  ) : (
                    Number.isFinite(deal.units) && deal.units > 0 && (
                      <p className={styles.units}>
                        <strong>{deal.units} units</strong> {copy.unitsLine || 'at this price.'}
                      </p>
                    )
                  )}

                  {bonus && (
                    <div className={styles.bonus}><SaleBonusPill bonus={bonus} theme="dark"/></div>
                  )}

                  {unavailable
                    ? <span className={styles.soldOut}>Out of stock</span>
                    : (
                      <SmartLink href={href} className={state === DEAL_LIVE ? styles.buy : styles.view}>
                        {state === DEAL_LIVE ? 'Get this deal' : (state === DEAL_SOLD_OUT ? (copy.ctaSoldOut || 'View product') : 'View product')}
                      </SmartLink>
                    )}
                </div>
              </li>
            )
          })}
        </ol>
        </div>
        ))}
        </div>
      </div>
    </section>
  )
}

export default SaleLimitedDeals
